import React, { useState, useRef, useEffect } from 'react';
import { ShopLocation, ImageMetadata } from '../types';
import AuthenticatedLocationImage from './AuthenticatedLocationImage';

interface LocationCardProps {
  location: ShopLocation;
  shopId: string;
  isSelected: boolean;
  onClick: () => void;
  onPlaceDrop?: (placeId: string, fromLocationId: string, toLocationId: string) => void;
  onSlideDrop?: (slideId: string, locationId: string) => void;
  onEdit?: (location: ShopLocation) => void;
  onDragHoverSelect?: (location: ShopLocation) => void;
}

const LocationCard: React.FC<LocationCardProps> = ({
  location,
  shopId,
  isSelected,
  onClick,
  onPlaceDrop,
  onSlideDrop,
  onEdit,
  onDragHoverSelect
}) => {
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Image carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageHovered, setImageHovered] = useState(false);
  const [prefetchTriggered, setPrefetchTriggered] = useState(false);
  const swipedRef = useRef(false);
  const touchStartRef = useRef<number | null>(null);
  const touchEndRef = useRef<number | null>(null);
  const dragStartRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const minSwipeDistance = 30;

  const sortedImages: ImageMetadata[] = location.images || [];
  const hasImages = sortedImages.length > 0;
  const hasMultipleImages = sortedImages.length > 1;

  // Reset index if images change
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [location.id]);

  const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;
  const showArrows = hasMultipleImages && imageHovered && !isTouchDevice;

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPrefetchTriggered(true);
    setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPrefetchTriggered(true);
    setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    touchEndRef.current = null;
    touchStartRef.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndRef.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (touchStartRef.current === null || touchEndRef.current === null || !hasMultipleImages) {
      touchStartRef.current = null;
      touchEndRef.current = null;
      return;
    }
    const distance = touchStartRef.current - touchEndRef.current;
    if (distance > minSwipeDistance) {
      setPrefetchTriggered(true);
      setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
      swipedRef.current = true;
    } else if (distance < -minSwipeDistance) {
      setPrefetchTriggered(true);
      setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
      swipedRef.current = true;
    }
    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  const onImageMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    dragStartRef.current = e.clientX;
    isDraggingRef.current = false;
  };

  const onImageMouseMove = (e: React.MouseEvent) => {
    if (dragStartRef.current !== null && Math.abs(e.clientX - dragStartRef.current) > 5) {
      isDraggingRef.current = true;
    }
  };

  const onImageMouseUp = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dragStartRef.current !== null && isDraggingRef.current && hasMultipleImages) {
      const distance = dragStartRef.current - e.clientX;
      if (distance > minSwipeDistance) {
        setPrefetchTriggered(true);
        setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
        swipedRef.current = true;
      } else if (distance < -minSwipeDistance) {
        setPrefetchTriggered(true);
        setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
        swipedRef.current = true;
      }
    }
    dragStartRef.current = null;
    isDraggingRef.current = false;
  };

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!isDragOver) {
      setIsDragOver(true);

      // Start hover timer to auto-select this location after 800ms
      if (onDragHoverSelect && !isSelected) {
        hoverTimerRef.current = setTimeout(() => {
          console.log('Auto-selecting location after drag hover:', location.name);
          onDragHoverSelect(location);
        }, 800);
      }
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    // Clear hover timer when drag leaves the card
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    // Clear hover timer on drop
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));

      // Check if this is a slide drop
      if (data.slideId) {
        if (onSlideDrop) {
          onSlideDrop(data.slideId, location.id);
        }
        return;
      }

      // Otherwise, handle place drop
      const { placeId, fromLocationId } = data;

      // Don't allow dropping on the same location
      if (fromLocationId === location.id) {
        return;
      }

      if (onPlaceDrop && placeId && fromLocationId) {
        onPlaceDrop(placeId, fromLocationId, location.id);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  return (
    <div
      onClick={onClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        backgroundColor: isDragOver ? 'var(--success-bg)' : (isSelected ? 'var(--bg-card)' : 'var(--bg-primary)'),
        border: isDragOver ? '2px dashed var(--success)' : (isSelected ? '2px solid var(--primary)' : '1px solid var(--border-light)'),
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        boxShadow: isDragOver
          ? '0 4px 12px rgba(76, 175, 80, 0.3)'
          : (isSelected
            ? '0 4px 6px rgba(139, 69, 19, 0.15)'
            : '0 1px 3px rgba(139, 69, 19, 0.05)'),
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
          e.currentTarget.style.borderColor = 'var(--primary-light)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
          e.currentTarget.style.borderColor = 'var(--border-light)';
        }
      }}
    >
      {/* Image carousel on top */}
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => {
          setImageHovered(true);
          if (!prefetchTriggered && hasMultipleImages) setPrefetchTriggered(true);
        }}
        onMouseLeave={() => setImageHovered(false)}
        onTouchStart={hasMultipleImages ? onTouchStart : undefined}
        onTouchMove={hasMultipleImages ? onTouchMove : undefined}
        onTouchEnd={hasMultipleImages ? onTouchEnd : undefined}
        onMouseDown={hasMultipleImages ? onImageMouseDown : undefined}
        onMouseMove={hasMultipleImages ? onImageMouseMove : undefined}
        onMouseUp={hasMultipleImages ? onImageMouseUp : undefined}
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '4px 4px 0 0',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
      >
        {hasImages ? (
          sortedImages.map((img, index) => {
            if (index !== currentImageIndex && !prefetchTriggered) return null;
            return (
              <AuthenticatedLocationImage
                key={img.id}
                shopId={shopId}
                locationId={location.id}
                imageId={img.id}
                alt={location.name}
                size="thumb_300"
                style={{
                  width: '100%',
                  aspectRatio: '1/1',
                  objectFit: 'cover',
                  pointerEvents: 'none',
                  ...(index !== currentImageIndex ? { position: 'absolute', visibility: 'hidden' } : {})
                }}
              />
            );
          })
        ) : (
          <div
            style={{
              width: '100%',
              aspectRatio: '1/1',
              backgroundColor: 'var(--bg-secondary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img src="/logo-192.png" alt="Carousel Shop AI" style={{ width: '48px', height: '48px', opacity: 0.35, borderRadius: '8px' }} />
          </div>
        )}

        {/* Edit button overlaid on image */}
        {onEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(location);
            }}
            style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              background: 'rgba(255, 255, 255, 0.85)',
              border: 'none',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--text-accent)',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
              zIndex: 2
            }}
            title="Edit location"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5 1.5L12.5 3.5L4.5 11.5H2.5V9.5L10.5 1.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 3L11 5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}

        {/* Left arrow */}
        {hasMultipleImages && (
          <button
            onClick={handlePrevImage}
            style={{
              position: 'absolute',
              left: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255, 255, 255, 0.8)',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              color: 'var(--text-primary)',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
              opacity: showArrows ? 0.9 : 0,
              transition: 'opacity 0.2s ease',
              zIndex: 1,
              padding: 0,
              lineHeight: 1,
              WebkitTapHighlightColor: 'transparent'
            }}
            aria-label="Previous image"
          >
            &#8249;
          </button>
        )}

        {/* Right arrow */}
        {hasMultipleImages && (
          <button
            onClick={handleNextImage}
            style={{
              position: 'absolute',
              right: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255, 255, 255, 0.8)',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              color: 'var(--text-primary)',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
              opacity: showArrows ? 0.9 : 0,
              transition: 'opacity 0.2s ease',
              zIndex: 1,
              padding: 0,
              lineHeight: 1,
              WebkitTapHighlightColor: 'transparent'
            }}
            aria-label="Next image"
          >
            &#8250;
          </button>
        )}

        {/* Dot indicators */}
        {hasMultipleImages && (
          <div
            style={{
              position: 'absolute',
              bottom: '4px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 6px',
              borderRadius: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1
            }}
          >
            {sortedImages.slice(0, 5).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: index === currentImageIndex ? 'var(--bg-card)' : 'rgba(255, 255, 255, 0.5)',
                  transition: 'background-color 0.2s ease'
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content below image */}
      <div style={{ padding: '8px 10px 10px' }}>
        {/* Location Name */}
        <div
          style={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: 'var(--text-primary)',
            lineHeight: '1.3',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            maskImage: 'linear-gradient(to right, black 80%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, black 80%, transparent 100%)'
          }}
        >
          {location.name}
        </div>

        {/* Location Description */}
        {location.description && (
          <div
            style={{
              fontSize: '0.75rem',
              color: 'var(--text-secondary)',
              lineHeight: '1.4',
              marginTop: '2px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              maskImage: 'linear-gradient(to right, black 80%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to right, black 80%, transparent 100%)'
            }}
          >
            {location.description}
          </div>
        )}

        {/* Place Count */}
        <div
          style={{
            fontSize: '0.6875rem',
            color: 'var(--text-secondary)',
            marginTop: '4px'
          }}
        >
          {location.places?.length || 0} places
        </div>

        {/* Active Status Badge */}
        {!location.active && (
          <div
            style={{
              marginTop: '4px',
              padding: '2px 6px',
              backgroundColor: 'var(--error-bg)',
              border: '1px solid var(--error-border)',
              borderRadius: '3px',
              fontSize: '0.625rem',
              fontWeight: 600,
              color: 'var(--error-text)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'inline-block'
            }}
          >
            Inactive
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationCard;
